import { useEffect, useCallback } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export function useSessionManagement(user: User | null) {
  // Generate a unique device/session ID
  const getDeviceId = useCallback(() => {
    let deviceId = localStorage.getItem("paystreem_device_id");
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("paystreem_device_id", deviceId);
    }
    return deviceId;
  }, []);

  // Update session activity
  const updateSessionActivity = useCallback(async () => {
    if (!user) return;

    try {
      const deviceId = getDeviceId();
      console.log(
        "📱 Updating session activity for user:",
        user.id,
        "device:",
        deviceId,
      );

      const { error } = await supabase.rpc("upsert_user_session", {
        p_user_id: user.id,
        p_session_id: deviceId,
        p_device_info: navigator.userAgent,
        p_ip_address: null,
      });

      if (error) {
        console.error("❌ Error updating session activity:", error);
      } else {
        console.log("✅ Session activity updated successfully");
      }
    } catch (error) {
      console.error("💥 Session update failed:", error);
    }
  }, [user, getDeviceId]);

  // Remove session on cleanup
  const removeSession = useCallback(async () => {
    try {
      const deviceId = getDeviceId();
      console.log("🗑️ Removing session for device:", deviceId);

      const { error } = await supabase.rpc("remove_user_session", {
        p_session_id: deviceId,
      });

      if (error) {
        console.error("❌ Error removing session:", error);
      } else {
        console.log("✅ Session removed successfully");
      }
    } catch (error) {
      console.error("💥 Session removal failed:", error);
    }
  }, [getDeviceId]);

  // Initialize and manage session
  useEffect(() => {
    if (!user) return;

    console.log("🚀 Initializing session management for user:", user.id);

    // Update session on mount
    updateSessionActivity();

    // Set up periodic session updates (every 2 minutes to stay well under 5-minute threshold)
    const interval = setInterval(
      () => {
        console.log("⏰ Periodic session update");
        updateSessionActivity();
      },
      2 * 60 * 1000,
    );

    // Update session on user activity
    const handleActivity = () => {
      console.log("👆 User activity detected, updating session");
      updateSessionActivity();
    };

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    let activityTimeout: NodeJS.Timeout;

    const debouncedActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(handleActivity, 30000); // Debounce for 30 seconds
    };

    events.forEach((event) => {
      document.addEventListener(event, debouncedActivity, true);
    });

    // Cleanup on unmount or user change
    return () => {
      console.log("🧹 Cleaning up session management");
      clearInterval(interval);
      clearTimeout(activityTimeout);
      events.forEach((event) => {
        document.removeEventListener(event, debouncedActivity, true);
      });
      removeSession();
    };
  }, [user, updateSessionActivity, removeSession]);

  // Handle page visibility changes
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("👀 Page became visible, updating session");
        updateSessionActivity();
      } else {
        console.log("🙈 Page became hidden");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [user, updateSessionActivity]);

  return {
    updateSessionActivity,
    removeSession,
  };
}
